
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

// Free users get 5 messages per video
const FREE_MESSAGE_LIMIT = 5;

export const useChatLimit = (videoId: string) => {
  const [remainingMessages, setRemainingMessages] = useState<number>(FREE_MESSAGE_LIMIT);
  const [showLimitPopup, setShowLimitPopup] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasSubscription, setHasSubscription] = useState<boolean>(false);

  // Check if user has an active subscription
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        setIsLoading(true);
        
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setRemainingMessages(FREE_MESSAGE_LIMIT);
          setHasSubscription(false);
          setIsLoading(false);
          return;
        }
        
        // Check if user has an active subscription
        const { data: subscription } = await supabase
          .from('user_subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .eq('payment_status', 'active')
          .maybeSingle();
          
        if (subscription) {
          // User has a subscription, unlimited messages
          setHasSubscription(true);
          setRemainingMessages(Infinity);
        } else {
          // Free user, check how many messages used for this video
          const { data: videoUsage } = await supabase
            .from('video_message_usage')
            .select('messages_used')
            .eq('user_id', user.id)
            .eq('video_id', videoId)
            .maybeSingle();
            
          if (videoUsage) {
            const remaining = Math.max(0, FREE_MESSAGE_LIMIT - videoUsage.messages_used);
            setRemainingMessages(remaining);
            
            // Show popup if limit reached
            if (remaining <= 0) {
              setShowLimitPopup(true);
            }
          } else {
            // No usage record yet
            setRemainingMessages(FREE_MESSAGE_LIMIT);
          }
        }
      } catch (error) {
        console.error("Error checking subscription:", error);
        // Fallback to free limit
        setRemainingMessages(FREE_MESSAGE_LIMIT);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (videoId) {
      checkSubscription();
    }
  }, [videoId]);
  
  // Decrement remaining messages
  const decrementRemainingMessages = () => {
    if (hasSubscription) return; // No limit for subscribers
    
    const newRemaining = remainingMessages - 1;
    setRemainingMessages(newRemaining);
    
    // Show popup when limit reached
    if (newRemaining <= 0) {
      setShowLimitPopup(true);
    }
  };
  
  return {
    remainingMessages,
    hasSubscription,
    isLoading,
    showLimitPopup,
    setShowLimitPopup,
    decrementRemainingMessages
  };
};
